import React from 'react';
import { createPortal } from 'react-dom';
import { AgnesButton } from '@agnes/ui';
import { DctfEmpregadorSelect } from '@/components/encargos/DctfEmpregadorSelect';
import ServiceDctf from '@/services/ServiceDctf';
import UtilsMask from '@/utils/UtilsMask';
import { mensagemErro } from '@/utils/apiError';

type Any = any;

interface Props {
  open: boolean;
  employers: Any[];
  onCancel: () => void;
  onExecuted: (cnpjs: string[]) => void;
}

interface CollectionResult {
  cnpj: string;
  success: boolean;
  message: string;
}

const MONTHS = [
  ['01', 'Janeiro'],
  ['02', 'Fevereiro'],
  ['03', 'Março'],
  ['04', 'Abril'],
  ['05', 'Maio'],
  ['06', 'Junho'],
  ['07', 'Julho'],
  ['08', 'Agosto'],
  ['09', 'Setembro'],
  ['10', 'Outubro'],
  ['11', 'Novembro'],
  ['12', 'Dezembro'],
] as const;

const currentPeriod = () => {
  const now = new Date();
  return { year: String(now.getFullYear()), month: String(now.getMonth() + 1).padStart(2, '0') };
};

// Competência futura ainda não tem DCTF transmitida: a coleta vai até o mês corrente.
const isFuturePeriod = (year: string, month: string) => {
  const now = new Date();
  const selectedYear = Number(year);
  return (
    selectedYear > now.getFullYear() ||
    (selectedYear === now.getFullYear() && Number(month) > now.getMonth() + 1)
  );
};

export default function DctfCollectionModal({ open, employers, onCancel, onExecuted }: Props) {
  const allowedCnpjs = React.useMemo(
    () => new Set(employers.map((employer) => employer.cnpjFilial).filter(Boolean)),
    [employers],
  );
  const [selectedCnpjs, setSelectedCnpjs] = React.useState<string[]>([]);
  const [{ year, month }, setPeriod] = React.useState(currentPeriod);
  const [loading, setLoading] = React.useState(false);
  const [processed, setProcessed] = React.useState(0);
  const [currentCnpj, setCurrentCnpj] = React.useState('');
  const [results, setResults] = React.useState<CollectionResult[]>([]);
  const [resultMode, setResultMode] = React.useState(false);
  const eligibleCnpjs = selectedCnpjs.filter((cnpj) => allowedCnpjs.has(cnpj));

  React.useEffect(() => {
    if (!open) return;
    setSelectedCnpjs([]);
    setPeriod(currentPeriod());
    setLoading(false);
    setProcessed(0);
    setCurrentCnpj('');
    setResults([]);
    setResultMode(false);
  }, [open]);

  const canExecute =
    eligibleCnpjs.length > 0 &&
    Number(year) >= 2020 &&
    Boolean(month) &&
    !isFuturePeriod(year, month);

  const execute = async () => {
    const companies = eligibleCnpjs;
    if (companies.length === 0) return;

    setLoading(true);
    setProcessed(0);
    setCurrentCnpj('');
    setResults([]);

    const completed: CollectionResult[] = [];
    for (const [index, cnpj] of companies.entries()) {
      setCurrentCnpj(UtilsMask.cnpj(cnpj));
      let result: CollectionResult;
      try {
        await ServiceDctf.sincronizar(cnpj, Number(year), Number(month));
        result = { cnpj, success: true, message: 'Coleta realizada com sucesso.' };
      } catch (error) {
        result = {
          cnpj,
          success: false,
          message: mensagemErro(error, 'Houve um problema ao solicitar a atualização dos dados.'),
        };
      }
      completed.push(result);
      setResults([...completed]);
      setProcessed(index + 1);
    }

    setLoading(false);
    setCurrentCnpj('');
    setResultMode(true);
  };

  const closeResults = () => {
    const cnpjs = [...eligibleCnpjs];
    setResultMode(false);
    onExecuted(cnpjs);
  };

  if (!open) return null;

  const successCount = results.filter((result) => result.success).length;
  const errorCount = results.length - successCount;

  return createPortal(
    // z-[1300]: mesma camada do AgnesModal, abaixo da lista do AgnesAutocomplete (z-[1400]).
    <div className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="dctf-collection-title"
        className="max-h-[90vh] w-full max-w-4xl overflow-auto rounded-xl bg-agnes-bg-surface p-5 shadow-xl"
      >
        <header className="mb-4">
          <h2 id="dctf-collection-title" className="text-lg font-semibold text-agnes-text">
            Atualizar Dados
          </h2>
        </header>

        {resultMode ? (
          <>
            <div className="mb-4 flex flex-wrap gap-5 text-sm">
              <p>
                <strong data-testid="dctf-success-count">{successCount}</strong> empresas
                processadas com sucesso
              </p>
              <p>
                <strong data-testid="dctf-error-count">{errorCount}</strong> empresas apresentaram
                erro
              </p>
            </div>
            <div className="overflow-x-auto rounded-md border border-agnes-border">
              <table className="w-full text-left text-sm">
                <thead className="bg-agnes-bg-subtle">
                  <tr>
                    <th className="px-3 py-2">CNPJ</th>
                    <th className="px-3 py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result) => (
                    <tr key={result.cnpj} className="border-t border-agnes-border">
                      <td className="px-3 py-2">{UtilsMask.cnpj(result.cnpj)}</td>
                      <td className="px-3 py-2">
                        {result.success ? '✅ Sucesso — ' : '❌ '}
                        {result.message}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-5 flex justify-end">
              <AgnesButton variant="primary" onClick={closeResults}>
                Fechar
              </AgnesButton>
            </div>
          </>
        ) : (
          <>
            <p className="mb-4 text-sm text-agnes-text-secondary">
              Selecione as empresas e a competência para atualizar os dados da DCTF.
            </p>

            <DctfEmpregadorSelect
              label="Empregadores"
              placeholder="Selecionar empregadores"
              empregadores={employers}
              value={selectedCnpjs}
              disabled={loading}
              noOptionsText="Nenhuma empresa atribuída"
              onChange={(cnpjs) => setSelectedCnpjs(cnpjs.filter((cnpj) => allowedCnpjs.has(cnpj)))}
            />

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <label className="flex flex-col gap-1 text-sm font-medium text-agnes-text">
                Mês de apuração
                <select
                  aria-label="Mês de apuração"
                  value={month}
                  disabled={loading}
                  onChange={(event) =>
                    setPeriod((period) => ({ ...period, month: event.target.value }))
                  }
                  className="h-10 rounded-md border border-agnes-border bg-agnes-bg-surface px-3 font-normal"
                >
                  {MONTHS.map(([value, label]) => (
                    <option key={value} value={value} disabled={isFuturePeriod(year, value)}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1 text-sm font-medium text-agnes-text">
                Ano de apuração
                <input
                  aria-label="Ano de apuração"
                  type="number"
                  min={2020}
                  max={new Date().getFullYear()}
                  value={year}
                  disabled={loading}
                  onChange={(event) =>
                    setPeriod((period) => ({ ...period, year: event.target.value }))
                  }
                  className="h-10 rounded-md border border-agnes-border bg-agnes-bg-surface px-3 font-normal"
                />
              </label>
            </div>

            {loading && (
              <section
                role="status"
                aria-live="polite"
                className="my-5 rounded-lg border border-agnes-border p-4"
              >
                <div className="font-semibold">Processando coletas…</div>
                <div className="my-2 text-sm">
                  {processed} de {eligibleCnpjs.length}
                </div>
                <progress className="w-full" value={processed} max={eligibleCnpjs.length} />
                {currentCnpj && (
                  <p className="mt-2 text-sm">
                    Processando empresa: <strong>{currentCnpj}</strong>
                  </p>
                )}
              </section>
            )}

            <div className="mt-5 flex justify-end gap-2">
              <AgnesButton variant="secondary" disabled={loading} onClick={onCancel}>
                Cancelar
              </AgnesButton>
              <AgnesButton
                variant="primary"
                disabled={!canExecute || loading}
                onClick={() => void execute()}
              >
                {loading ? 'Atualizando…' : 'Executar'}
              </AgnesButton>
            </div>
          </>
        )}
      </section>
    </div>,
    document.body,
  );
}
